/**
 * @fileoverview A program viewer's Overview tab: the program at a glance.
 *
 * The classes come first, because they are what the program is: when and
 * where each meets, who teaches it and how many are on it. A class's
 * attendance grid (with the attendance grant) sits inside its card, closed to
 * start, so a program with many classes still reads as a list of them.
 *
 * Beside them: the instructors (their email and phone with the contacts
 * grant, the sessions each taught lately with performance), the latest lesson
 * notes with that grant, and the program's own details.
 */

import { useState } from "react";
import { Avatar, Card, Icon, SectionHeader } from "@/components/studio/primitives";
import { shortDateLabel } from "@/lib/schedule";
import { longDate, plural, scheduleLine } from "@/lib/viewerPortal";

const TH_BASE = "px-4 py-2.5 font-mono text-[11px] font-normal uppercase tracking-wide text-[var(--text-tertiary)]";
const note = "px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]";

/** The latest taught days a class's grid shows (services/program_viewer.ATTENDANCE_DAYS). */
const ATTENDANCE_DAYS = 8;

/** "Oct 13" from "2026-10-13": the weekday is dropped to keep grid columns narrow. */
const dayLabel = (iso) => shortDateLabel(iso).split(", ")[1];

const MARKS = {
  present: { icon: "check", label: "Present", className: "text-[var(--accent-yellow-text)]" },
  absent: { icon: "x", label: "Absent", className: "text-[var(--text-secondary)]" },
};

function AttendanceTable({ sheet }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-b border-[var(--studio-divider)]">
            <th scope="col" className={`${TH_BASE} text-left`}>Student</th>
            {sheet.dates.map((d) => (
              <th key={d} scope="col" className={`${TH_BASE} w-20 whitespace-nowrap text-center`}>{dayLabel(d)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sheet.students.map((st, idx) => (
            <tr key={`${st.name}-${idx}`} className={idx < sheet.students.length - 1 ? "border-b border-[var(--studio-divider)]" : undefined}>
              <td className="whitespace-nowrap px-4 py-2.5 font-sans text-[14px] text-[var(--text-primary)]">{st.name}</td>
              {st.marks.map((mark, i) => {
                const m = MARKS[mark];
                return (
                  <td key={sheet.dates[i]} className="px-2 py-2.5 text-center">
                    {m ? (
                      <Icon name={m.icon} size={15} className={`mx-auto ${m.className}`} aria-label={m.label} />
                    ) : (
                      <span className="font-sans text-[14px] text-[var(--text-tertiary)]" aria-label="Not marked">–</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A class's attendance inside its card: one line that opens to the grid.
 * Closed to start; a class with nothing taken yet says so and has nothing to open.
 */
function ClassAttendance({ sheet }) {
  const [open, setOpen] = useState(false);
  if (sheet.dates.length === 0 || sheet.students.length === 0) {
    return (
      <div className="border-t border-[var(--studio-divider)] px-[18px] py-3 font-sans text-[13px] text-[var(--text-tertiary)]">
        No attendance taken yet.
      </div>
    );
  }
  return (
    <div className="border-t border-[var(--studio-divider)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`viewer-attendance-toggle-${sheet.class_id}`}
        className={`flex w-full items-center gap-2 px-[18px] py-3 text-left font-sans text-[14px] text-[var(--text-secondary)] transition-colors duration-150 hover:bg-[var(--studio-overlay)] ${
          open ? "" : "rounded-b-[inherit]"
        }`}
      >
        <Icon name="check" size={15} className="text-[var(--text-tertiary)]" />
        <span className="flex-1">
          Attendance <span className="text-[var(--text-tertiary)]">· {plural(sheet.total_days, "session")}</span>
        </span>
        <Icon name={open ? "chevronUp" : "chevronDown"} size={17} className="shrink-0 text-[var(--text-tertiary)]" />
      </button>
      {open && (
        <div className="border-t border-[var(--studio-divider)]" data-testid={`viewer-attendance-${sheet.class_id}`}>
          <div className="flex items-center justify-end gap-3 px-[18px] pb-1 pt-3 font-sans text-[12px] text-[var(--text-tertiary)]">
            <span className="inline-flex items-center gap-1">
              <Icon name="check" size={13} className="text-[var(--accent-yellow-text)]" /> Present
            </span>
            <span className="inline-flex items-center gap-1">
              <Icon name="x" size={13} /> Absent
            </span>
          </div>
          <AttendanceTable sheet={sheet} />
          {sheet.total_days > sheet.dates.length && (
            <div className="border-t border-[var(--studio-divider)] px-[18px] py-2.5 font-sans text-[12px] text-[var(--text-tertiary)]">
              The latest {sheet.dates.length} of {plural(sheet.total_days, "session")}.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ClassCard({ cls, sheet }) {
  const where = [scheduleLine(cls.schedule), cls.room || cls.schedule?.room].filter(Boolean).join(" · ");
  return (
    <Card padding={0} className="mb-3" data-testid={`viewer-class-${cls.class_id}`}>
      <div className="flex items-start gap-3 px-[18px] py-4">
        <div className="min-w-0 flex-1">
          <div className="font-sans text-[16px] font-semibold text-[var(--text-primary)]">{cls.name}</div>
          {where && <div className="mt-1 font-sans text-[13px] text-[var(--text-secondary)]">{where}</div>}
          <div className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">{cls.instructor_name || "No instructor yet"}</div>
        </div>
        <span className="shrink-0 font-sans text-[13px] text-[var(--text-tertiary)]">{plural(cls.member_count ?? 0, "student")}</span>
      </div>
      {sheet && <ClassAttendance sheet={sheet} />}
    </Card>
  );
}

/** The program's instructors: how to reach them (contacts grant), and what each taught lately (performance). */
function InstructorsCard({ instructors, performance }) {
  const taught = new Map((performance?.by_instructor || []).map((r) => [r.name, r.sessions]));
  const rows = instructors
    ? instructors.map((i) => ({ key: i.user_id, name: i.name, email: i.email, phone: i.phone }))
    : (performance?.by_instructor || []).map((r) => ({ key: r.name, name: r.name }));
  if (!rows.length && !instructors) return null;
  return (
    <section>
      <SectionHeader>{instructors ? "Instructors" : "Teaching"}</SectionHeader>
      <Card padding={0} data-testid="viewer-instructors">
        {rows.length === 0 ? (
          <div className={note}>No instructors yet.</div>
        ) : (
          rows.map((r, idx) => (
            <div
              key={r.key}
              data-testid={`viewer-instructor-${r.key}`}
              className={`flex items-start gap-3 px-[18px] py-3.5 ${idx < rows.length - 1 ? "border-b border-[var(--studio-divider)]" : ""}`}
            >
              <Avatar name={r.name} size={36} />
              <div className="min-w-0 flex-1">
                <div className="font-sans text-[15px] font-medium text-[var(--text-primary)]">{r.name}</div>
                {taught.has(r.name) && (
                  <div className="font-sans text-[13px] text-[var(--text-tertiary)]">
                    {plural(taught.get(r.name), "session")} in the {performance.window_label.toLowerCase()}
                  </div>
                )}
                {(r.email || r.phone) && (
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 font-sans text-[13px]">
                    {r.email && (
                      <a href={`mailto:${r.email}`} className="truncate text-[var(--accent-yellow-text)] hover:underline">{r.email}</a>
                    )}
                    {r.phone && (
                      <a href={`tel:${r.phone}`} className="text-[var(--accent-yellow-text)] hover:underline">{r.phone}</a>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
        {performance?.substitute_sessions > 0 && (
          <div className="border-t border-[var(--studio-divider)] px-[18px] py-3 font-sans text-[13px] text-[var(--text-tertiary)]">
            {plural(performance.substitute_sessions, "session")} covered by a substitute.
          </div>
        )}
      </Card>
    </section>
  );
}

function LatestNotes({ data, onSeeAll }) {
  // The latest three (the server sends them newest first), read oldest first
  // like every lesson list (lib/lessonHistory).
  const latest = (data.notes || []).slice(0, 3).reverse();
  return (
    <section>
      <SectionHeader action={data.notes?.length ? "All lessons" : undefined} onAction={onSeeAll} actionTestId="viewer-notes-see-all">
        Latest lesson notes
      </SectionHeader>
      <Card padding={0} data-testid="viewer-latest-notes">
        {latest.length === 0 ? (
          <div className={note}>No lesson notes shared yet.</div>
        ) : (
          latest.map((n, idx) => (
            <div key={n.lesson_id} className={`px-[18px] py-3.5 ${idx < latest.length - 1 ? "border-b border-[var(--studio-divider)]" : ""}`}>
              <div className="font-sans text-[14px] font-medium text-[var(--text-primary)]">{n.title || n.class_name}</div>
              <div className="mt-0.5 font-sans text-[12px] text-[var(--text-tertiary)]">
                {[shortDateLabel(n.date), n.class_name].join(" · ")}
              </div>
              <p className="mt-1.5 line-clamp-2 font-sans text-[13px] leading-relaxed text-[var(--text-secondary)]">{n.summary}</p>
            </div>
          ))
        )}
      </Card>
    </section>
  );
}

function AboutCard({ program }) {
  const term = [longDate(program.start_date), longDate(program.end_date)].filter(Boolean).join(" – ");
  return (
    <section>
      <SectionHeader>About the program</SectionHeader>
      <Card padding={0} data-testid="viewer-about">
        {program.description && (
          <p className="border-b border-[var(--studio-divider)] px-[18px] py-3.5 font-sans text-[14px] leading-relaxed text-[var(--text-secondary)]">
            {program.description}
          </p>
        )}
        {[
          program.location && { icon: "mapPin", text: program.location },
          term && { icon: "calendar", text: term },
        ]
          .filter(Boolean)
          .map((row, i, rows) => (
            <div
              key={row.icon}
              className={`flex items-center gap-3 px-[18px] py-3 font-sans text-[14px] text-[var(--text-primary)] ${
                i < rows.length - 1 ? "border-b border-[var(--studio-divider)]" : ""
              }`}
            >
              <Icon name={row.icon} size={16} className="shrink-0 text-[var(--text-tertiary)]" />
              {row.text}
            </div>
          ))}
      </Card>
    </section>
  );
}

/**
 * @param {{ data: object, onOpenTab: (tab: string) => void }} props `data` is
 *   `GET /org/viewer/programs/{id}`: only the sections the viewer was granted.
 */
export default function ViewerOverview({ data, onOpenTab }) {
  const { program, classes = [], attendance, instructors, performance, lesson_notes: lessonNotes } = data;
  const sheets = new Map((attendance || []).map((s) => [s.class_id, s]));
  const side = Boolean(instructors || performance?.by_instructor?.length);
  return (
    <div className="grid items-start gap-6 xl:grid-cols-3" data-testid="viewer-overview">
      <section className="min-w-0 xl:col-span-2">
        <SectionHeader>Classes ({classes.length})</SectionHeader>
        {classes.length === 0 ? (
          <Card>
            <p className="font-sans text-[15px] text-[var(--text-tertiary)]">No classes in this program yet.</p>
          </Card>
        ) : (
          classes.map((c) => <ClassCard key={c.class_id} cls={c} sheet={sheets.get(c.class_id)} />)
        )}
        {attendance && (
          <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
            Open a class&rsquo;s attendance to see who was there on each of its latest {ATTENDANCE_DAYS} days.
          </p>
        )}
        {lessonNotes && (
          <div className="mt-6">
            <LatestNotes data={lessonNotes} onSeeAll={() => onOpenTab("lessons")} />
          </div>
        )}
      </section>
      <div className="flex min-w-0 flex-col gap-6">
        {side && <InstructorsCard instructors={instructors} performance={performance} />}
        <AboutCard program={program} />
      </div>
    </div>
  );
}
